import { useEffect, useState } from "react";
import { Store, Plus, Trash2, Loader2, Save, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Distribución de una Orden de Compra por tienda + descarga en PDF.
export default function RetailPODistribution({ item, onRefresh }) {
  const [stores, setStores] = useState([]);
  const [lineas, setLineas] = useState(item.distribucion?.length ? item.distribucion : [{ sku: "", descripcion: "", por_tienda: [{ tienda: "", nombre: "", piezas: "" }] }]);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/retail/stores").then((r) => setStores(r.data)).catch(() => {}); }, []);

  const setLine = (i, k, v) => setLineas((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLineas((ls) => [...ls, { sku: "", descripcion: "", por_tienda: [{ tienda: "", nombre: "", piezas: "" }] }]);
  const rmLine = (i) => setLineas((ls) => ls.filter((_, idx) => idx !== i));
  const setStore = (li, si, tiendaNumero, piezas) => setLineas((ls) => ls.map((l, idx) => {
    if (idx !== li) return l;
    const st = stores.find((s) => String(s.numero) === String(tiendaNumero));
    const pt = l.por_tienda.map((p, pi) => pi === si ? { tienda: tiendaNumero, nombre: st?.nombre || "", piezas: piezas ?? p.piezas } : p);
    return { ...l, por_tienda: pt };
  }));
  const addStoreRow = (li) => setLineas((ls) => ls.map((l, idx) => idx === li ? { ...l, por_tienda: [...l.por_tienda, { tienda: "", nombre: "", piezas: "" }] } : l));
  const lineTotal = (l) => l.por_tienda.reduce((a, p) => a + (Number(p.piezas) || 0), 0);

  const save = async () => {
    setBusy("save");
    try {
      const payload = { lineas: lineas.filter((l) => l.sku.trim()).map((l) => ({ sku: l.sku.trim(), descripcion: l.descripcion, por_tienda: l.por_tienda.filter((p) => p.tienda && Number(p.piezas) > 0).map((p) => ({ tienda: p.tienda, nombre: p.nombre, piezas: Number(p.piezas) })) })) };
      const { data } = await api.put(`/retail/purchase-orders/${item.id}/distribution`, payload);
      toast.success(`Distribución guardada · ${data.piezas} piezas`);
      onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(""); }
  };

  const downloadPDF = async () => {
    setBusy("pdf");
    try {
      const res = await api.get(`/retail/purchase-orders/${item.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `OC-${item.folio || item.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el PDF"); } finally { setBusy(""); }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4" data-testid="po-distribution">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 overline text-navy/40"><Store className="w-3.5 h-3.5" /> Distribución por tienda</div>
        <button onClick={downloadPDF} disabled={busy === "pdf"} className="btn btn-ghost text-sm" data-testid="po-pdf">
          {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF
        </button>
      </div>
      {stores.length === 0 && <p className="text-xs text-amber-600 mb-2">Crea tus tiendas en <b>RETAIL Studio → Tiendas</b> para asignar cantidades.</p>}

      <div className="space-y-3 mb-2">
        {lineas.map((l, li) => (
          <div key={li} className="bg-white border border-navy/10 rounded-lg p-3" data-testid={`po-line-${li}`}>
            <div className="flex gap-2 mb-2">
              <input value={l.sku} onChange={(e) => setLine(li, "sku", e.target.value)} placeholder="SKU" className="w-28 field-sm" data-testid={`po-sku-${li}`} />
              <input value={l.descripcion || ""} onChange={(e) => setLine(li, "descripcion", e.target.value)} placeholder="Descripción" className="flex-1 field-sm" />
              <span className="text-sm text-navy/50 self-center whitespace-nowrap">Total: <b>{lineTotal(l)}</b></span>
              <button onClick={() => rmLine(li)} className="text-navy/30 hover:text-destructive self-center"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="space-y-1.5 pl-2 border-l-2 border-violet/20">
              {l.por_tienda.map((p, si) => (
                <div key={si} className="flex gap-2 items-center">
                  <select value={p.tienda} onChange={(e) => setStore(li, si, e.target.value, p.piezas)} className="field-sm flex-1" data-testid={`po-store-${li}-${si}`}>
                    <option value="">Tienda…</option>
                    {stores.map((s) => <option key={s.id} value={s.numero}>{s.numero} · {s.nombre}</option>)}
                  </select>
                  <input type="number" min="0" value={p.piezas} onChange={(e) => setStore(li, si, p.tienda, e.target.value)} placeholder="Pzs" className="w-20 field-sm" data-testid={`po-pieces-${li}-${si}`} />
                </div>
              ))}
              <button onClick={() => addStoreRow(li)} className="btn btn-ghost text-xs"><Plus className="w-3 h-3" /> Tienda</button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={addLine} className="btn btn-outline text-sm"><Plus className="w-4 h-4" /> SKU</button>
        <button onClick={save} disabled={busy === "save"} className="btn btn-primary text-sm ml-auto" data-testid="po-save-dist">
          {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar distribución
        </button>
      </div>
    </div>
  );
}
